import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from './api'
import type { Cart } from './types'
import { faroEvent } from './faro'

interface BagState {
  cart: Cart | null
  count: number
  busy: boolean
  add: (sku: string, color: string, size: string, qty?: number) => Promise<void>
  update: (line: string, qty: number) => Promise<void>
  remove: (line: string) => Promise<void>
  applyPromo: (code: string) => Promise<void>
  reset: () => void
  refresh: () => Promise<void>
}

const Ctx = createContext<BagState | null>(null)
const KEY = 'ensemble.cart'

function readId(): string | null {
  try { return localStorage.getItem(KEY) } catch { return null }
}
function writeId(id: string | null) {
  try { id ? localStorage.setItem(KEY, id) : localStorage.removeItem(KEY) } catch { /* private mode */ }
}

export function BagProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null)
  const [busy, setBusy] = useState(false)

  const refresh = useCallback(async () => {
    const id = readId()
    if (!id) { setCart(null); return }
    try { setCart(await api.cart(id)) } catch { writeId(null); setCart(null) }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const ensure = useCallback(async (): Promise<string> => {
    const id = readId()
    if (id) return id
    const c = await api.createCart()
    writeId(c.id)
    setCart(c)
    return c.id
  }, [])

  const run = useCallback(async (fn: (id: string) => Promise<Cart>) => {
    setBusy(true)
    try {
      const id = await ensure()
      setCart(await fn(id))
    } finally { setBusy(false) }
  }, [ensure])

  const value = useMemo<BagState>(() => ({
    cart, busy,
    count: cart?.items.reduce((n, i) => n + i.qty, 0) ?? 0,
    add: (sku, color, size, qty = 1) => run(id => api.addItem(id, sku, color, size, qty)).then(() => faroEvent('add_to_bag', { sku, size, color })),
    update: (line, qty) => run(id => api.updateItem(id, line, qty)),
    remove: (line) => run(id => api.removeItem(id, line)),
    applyPromo: (code) => run(id => api.promo(id, code)),
    reset: () => { writeId(null); setCart(null) },
    refresh,
  }), [cart, busy, run, refresh])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useBag(): BagState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useBag outside BagProvider')
  return v
}
